import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import SeatSelectButton from '../SeatSelectButton';
import Price from '../Price';
import { getDisplayPrice } from '../../../utils/getDisplayPrice';
import { FormattedMessage } from 'react-intl';

import { ReactComponent as Close } from '../../../svg/close.svg';

import FocusTrap from 'focus-trap-react';
import MediaQuery from 'react-responsive';
import Amenities from './Amenities';

import {
  acceptSeat,
  removeSeatFromBasket,
  updateSelectedSeat,
  nextPassenger,
  setCurrentPassenger,
  confirmBasket,
} from '../../../redux/actions';
import { isMobile } from 'react-device-detect';
import { rtlLangs } from '../../../translations/rtl-langs';
import { DetailsContainer } from './DetailsContainer';
import { Benefits } from './Benefits';
import { isNewSeatmapDesign } from '../../../utils/seatmapDesign';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { Carousel } from 'react-responsive-carousel';

const benefitsList = (benefits, limit) => {
  const list = [];
  benefits.forEach((benefit, index) => {
    list.push(
      <li id={`seat_details_benefit_${index}`} className="" key={index}>
        {benefit}
      </li>
    );
  });
  return (
    <ul
      id="seat-benefits-list"
      className={`gr-list-disc gr-ml-1 gr-pl-1 gr-mt-1`}
    >
      {limit ? list.slice(0, limit) : list}
    </ul>
  );
};

// #region Desktop

const DesktopSeatDetailsComponent = ({
  locale,
  rtlLangs,
  seat,
  test_seatmap_one_click,
  selected_seat,
  ariaPassenger,
  updateSelectedSeat,
  passenger_name,
  passengerInitials,
  display_name,
  categoryFill,
  imagesToDisplay,
  currency,
  displayPrice,
  priceFactor,
  decimal_places,
  usePriceAdjust,
  priceAdjustment,
  test_seatmap_one_click_desktop,
  SeatButton,
  useReservationFeeName,
  reservationFee,
  characteristics,
  description,
}) => (
  <div id="desktop-seat-details" className="gr-flex gr-flex-col">
    {/* Seat image — single image with category color accent below */}
    {imagesToDisplay?.[0] ? (
      <div style={{ lineHeight: 0 }}>
        <img
          src={imagesToDisplay[0]}
          alt=""
          style={{
            width: '100%',
            height: '180px',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        {categoryFill && (
          <div style={{ height: '4px', backgroundColor: categoryFill }} />
        )}
      </div>
    ) : categoryFill ? (
      <div style={{ height: '4px', backgroundColor: categoryFill }} />
    ) : null}
    {/* Header: seat + price + close */}
    <div
      className="gr-flex gr-items-start gr-justify-between gr-px-4 gr-border-b gr-border-gray-100"
      style={{ paddingTop: '14px', paddingBottom: '12px' }}
    >
      <div
        className="gr-flex-1"
        dir={rtlLangs.includes(locale) ? 'rtl' : 'ltr'}
      >
        <div
          className="gr-flex gr-items-baseline gr-justify-between"
          style={{ gap: '8px' }}
        >
          <h3
            className="gr-text-base gr-font-bold gr-text-gray-900"
            tabIndex={0}
          >
            <FormattedMessage id="seat" defaultMessage="Seat" />
            &nbsp;{seat}
          </h3>
          <span
            className="gr-text-base gr-font-bold gr-text-gray-900"
            tabIndex={0}
          >
            <Price
              currency={currency}
              amount={displayPrice * priceFactor}
              decimalPlaces={decimal_places}
            />
          </span>
        </div>
        <div className="gr-flex gr-items-center gr-mt-1" style={{ gap: '5px' }}>
          {categoryFill && (
            <span
              className="gr-rounded-full gr-flex-shrink-0"
              style={{
                width: '8px',
                height: '8px',
                backgroundColor: categoryFill,
              }}
            />
          )}
          {display_name && (
            <span className="gr-text-xs gr-text-gray-500">{display_name}</span>
          )}
          <span className="gr-text-xs gr-text-gray-400">· per person</span>
          {usePriceAdjust && (
            <span className="gr-text-xs gr-text-gray-400">
              (inc.{' '}
              <Price
                currency={currency}
                amount={displayPrice * (priceFactor - 1.0)}
                decimalPlaces={decimal_places}
              />{' '}
              {priceAdjustment.name})
            </span>
          )}
        </div>
      </div>
      {!test_seatmap_one_click ? (
        <button
          id="close-seat-details"
          aria-label={`Close details for seat ${selected_seat} for ${ariaPassenger}`}
          className="gr-p-1 gr-rounded-full gr-text-gray-400 hover:gr-text-gray-600 hover:gr-bg-gray-100"
          style={{ marginLeft: '8px', marginTop: '1px' }}
          onClick={(e) => {
            e.preventDefault();
            updateSelectedSeat(null);
          }}
        >
          <Close alt="close" style={{ width: 14, height: 14 }} />
        </button>
      ) : null}
    </div>

    {/* Passenger */}
    <div
      className="gr-flex gr-items-center gr-px-4 gr-border-b gr-border-gray-100"
      style={{ paddingTop: '10px', paddingBottom: '10px', gap: '10px' }}
    >
      <div
        className="gr-rounded-full gr-flex gr-items-center gr-justify-center gr-text-white gr-text-xs gr-font-bold gr-flex-shrink-0"
        style={{
          width: '28px',
          height: '28px',
          backgroundColor: 'var(--gordian-accent-color, #111827)',
        }}
      >
        {passengerInitials}
      </div>
      <span className="gr-text-sm gr-text-gray-700" tabIndex={0}>
        {passenger_name}
      </span>
    </div>

    {/* Category description + Amenities */}
    {(description || characteristics?.length > 0) && (
      <div
        className="gr-px-4 gr-border-b gr-border-gray-100"
        style={{ paddingTop: '10px', paddingBottom: '10px' }}
      >
        {description && (
          <p
            className="gr-text-xs gr-text-gray-500"
            style={{
              marginBottom: characteristics?.length > 0 ? '8px' : 0,
              lineHeight: '1.5',
            }}
          >
            {description}
          </p>
        )}
        <Amenities characteristics={characteristics} desktop={true} />
      </div>
    )}

    {useReservationFeeName && (
      <div
        className="gr-px-4 gr-text-gray-400"
        style={{ paddingTop: '8px', fontSize: '11px' }}
      >
        {reservationFee}
      </div>
    )}

    {/* Select button */}
    {!test_seatmap_one_click_desktop && SeatButton && (
      <div
        className="gr-px-4"
        style={{ paddingTop: '10px', paddingBottom: '14px' }}
      >
        {SeatButton}
      </div>
    )}
  </div>
);

// Legacy (pre-redesign) desktop seat details.
const DesktopSeatDetailsComponentLegacy = ({
  desktopSeatDetailsClasses,
  locale,
  rtlLangs,
  seat,
  test_seatmap_one_click,
  selected_seat,
  ariaPassenger,
  updateSelectedSeat,
  passenger_name,
  currency,
  displayPrice,
  priceFactor,
  decimal_places,
  usePriceAdjust,
  priceAdjustment,
  test_seatmap_one_click_desktop,
  SeatButton,
  useReservationFeeName,
  reservationFee,
  characteristics,
}) => (
  <div id="desktop-seat-details" className={desktopSeatDetailsClasses}>
    <div className="gr-flex gr-justify-between gr-mb-2">
      <h3
        className="gr-text-base gr-text-title"
        tabIndex={0}
        dir={rtlLangs.includes(locale) ? 'rtl' : 'ltr'}
      >
        <FormattedMessage id="seat" defaultMessage="Seat" />
        &nbsp;
        {seat}
      </h3>
      {!test_seatmap_one_click ? (
        <button
          id="close-seat-details"
          aria-label={`Close details for seat ${selected_seat} for ${ariaPassenger}`}
          onClick={(e) => {
            e.preventDefault();
            updateSelectedSeat(null);
          }}
        >
          <Close
            alt="close"
            className="gr-cursor-pointer"
            style={{ width: 14, height: 14 }}
          />
        </button>
      ) : null}
    </div>
    <div className="gr-flex gr-justify-between gr-items-center gr-mt-2 gr-mb-4 gr-text-gray-700">
      <span tabIndex={0}>{passenger_name}</span>
      <span>
        <Price
          currency={currency}
          amount={displayPrice * priceFactor}
          decimalPlaces={decimal_places}
          tabIndex={0}
        />
        {usePriceAdjust && (
          <div className="gr-text-xs">
            (inc.{' '}
            <Price
              currency={currency}
              amount={displayPrice * (priceFactor - 1.0)}
              decimalPlaces={decimal_places}
              tabIndex={0}
            />{' '}
            {`${priceAdjustment.name}`})
          </div>
        )}
      </span>
    </div>
    {!test_seatmap_one_click_desktop ? SeatButton : null}
    {useReservationFeeName && (
      <div
        className="gr-flex gr-pt-1 gr-justify-center gr-text-gray-500"
        style={{ fontSize: '11.2px' }}
      >
        {reservationFee}
      </div>
    )}
    <Amenities characteristics={characteristics} desktop={true} />
  </div>
);

// #region EnhancedTitle

const EnhancedTitleSection = ({
  locale,
  rtlLangs,
  seat,
  display_name,
  test_seatmap_one_click,
  selected_seat,
  ariaPassenger,
  updateSelectedSeat,
  passenger_name,
  currency,
  displayPrice,
  priceFactor,
  decimal_places,
  usePriceAdjust,
  priceAdjustment,
}) => (
  <div>
    <div className=" gr-text-black gr-font-inter gr-font-bold">
      <div className="gr-flex gr-justify-between">
        <h3 tabIndex={0} dir={rtlLangs.includes(locale) ? 'rtl' : 'ltr'}>
          <FormattedMessage id="seat" defaultMessage="Seat" />
          &nbsp;
          {seat}
        </h3>
        <div>
          <h3 className="gr-mb-2 gr-text-blue-primary">{display_name}</h3>
        </div>
        {!test_seatmap_one_click ? (
          <button
            id="close-seat-details"
            aria-label={`Close details for seat ${selected_seat} for ${ariaPassenger}`}
            onClick={(e) => {
              e.preventDefault();
              updateSelectedSeat(null);
            }}
          >
            <Close
              alt="close"
              className="gr-cursor-pointer"
              style={{ width: 14, height: 14 }}
            />
          </button>
        ) : null}
      </div>
    </div>
    <div className="gr-flex gr-justify-between gr-items-center gr-text-gray-500">
      <span tabIndex={0}>{passenger_name}</span>
      <span>
        <Price
          currency={currency}
          amount={displayPrice * priceFactor}
          decimalPlaces={decimal_places}
          tabIndex={0}
        />
        {usePriceAdjust && (
          <div className="gr-text-xs">
            (inc.{' '}
            <Price
              currency={currency}
              amount={displayPrice * (priceFactor - 1.0)}
              decimalPlaces={decimal_places}
              tabIndex={0}
            />{' '}
            {`${priceAdjustment.name}`})
          </div>
        )}
      </span>
    </div>
  </div>
);

// #region DesktopEnhanced

const DesktopEnhancedSeatDetailsComponent = ({
  desktopSeatDetailsClasses,
  imagesToDisplay,
  test_seatmap_one_click_desktop,
  hasSeatDescriptions,
  SeatButton,
  useReservationFeeName,
  reservationFee,
  description,
  seatDescription,
  characteristics,
  locale,
  rtlLangs,
  seat,
  display_name,
  categoryFill,
  test_seatmap_one_click,
  selected_seat,
  ariaPassenger,
  updateSelectedSeat,
  passenger_name,
  passengerInitials,
  currency,
  displayPrice,
  priceFactor,
  decimal_places,
  usePriceAdjust,
  priceAdjustment,
}) => (
  <div id="desktop-seat-details" className={desktopSeatDetailsClasses}>
    {/* Seat image — first image only, no carousel */}
    {imagesToDisplay?.[0] && (
      <div style={{ lineHeight: 0 }}>
        <img
          src={imagesToDisplay[0]}
          alt=""
          style={{
            width: '100%',
            height: '180px',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        {categoryFill && (
          <div style={{ height: '4px', backgroundColor: categoryFill }} />
        )}
      </div>
    )}

    {/* Header: seat + price + close */}
    <div
      className="gr-flex gr-items-start gr-justify-between gr-px-4 gr-border-b gr-border-gray-100"
      style={{ paddingTop: '14px', paddingBottom: '12px' }}
    >
      <div
        className="gr-flex-1"
        dir={rtlLangs.includes(locale) ? 'rtl' : 'ltr'}
      >
        <div
          className="gr-flex gr-items-baseline gr-justify-between"
          style={{ gap: '8px' }}
        >
          <h3
            className="gr-text-base gr-font-bold gr-text-gray-900"
            tabIndex={0}
          >
            <FormattedMessage id="seat" defaultMessage="Seat" />
            &nbsp;{seat}
          </h3>
          <span
            className="gr-text-base gr-font-bold gr-text-gray-900"
            tabIndex={0}
          >
            <Price
              currency={currency}
              amount={displayPrice * priceFactor}
              decimalPlaces={decimal_places}
            />
          </span>
        </div>
        <div className="gr-flex gr-items-center gr-mt-1" style={{ gap: '5px' }}>
          {categoryFill && (
            <span
              className="gr-rounded-full gr-flex-shrink-0"
              style={{
                width: '8px',
                height: '8px',
                backgroundColor: categoryFill,
              }}
            />
          )}
          {display_name && (
            <span className="gr-text-xs gr-text-gray-500">{display_name}</span>
          )}
          <span className="gr-text-xs gr-text-gray-400">· per person</span>
        </div>
      </div>
      {!test_seatmap_one_click ? (
        <button
          id="close-seat-details"
          aria-label={`Close details for seat ${selected_seat} for ${ariaPassenger}`}
          className="gr-p-1 gr-rounded-full gr-text-gray-400 hover:gr-text-gray-600 hover:gr-bg-gray-100"
          style={{ marginLeft: '8px', marginTop: '1px' }}
          onClick={(e) => {
            e.preventDefault();
            updateSelectedSeat(null);
          }}
        >
          <Close alt="close" style={{ width: 14, height: 14 }} />
        </button>
      ) : null}
    </div>

    {/* Passenger */}
    <div
      className="gr-flex gr-items-center gr-px-4 gr-border-b gr-border-gray-100"
      style={{ paddingTop: '10px', paddingBottom: '10px', gap: '10px' }}
    >
      <div
        className="gr-rounded-full gr-flex gr-items-center gr-justify-center gr-text-white gr-text-xs gr-font-bold gr-flex-shrink-0"
        style={{
          width: '28px',
          height: '28px',
          backgroundColor: 'var(--gordian-accent-color, #111827)',
        }}
      >
        {passengerInitials}
      </div>
      <span className="gr-text-sm gr-text-gray-700" tabIndex={0}>
        {passenger_name}
      </span>
    </div>

    {/* Descriptions / amenities */}
    {(description || seatDescription || characteristics?.length > 0) && (
      <div
        className="gr-px-4 gr-border-b gr-border-gray-100"
        style={{ paddingTop: '10px', paddingBottom: '10px' }}
      >
        {description && (
          <p
            className="gr-text-xs gr-text-gray-500"
            style={{
              marginBottom:
                seatDescription || characteristics?.length > 0 ? '8px' : 0,
              lineHeight: '1.5',
            }}
          >
            {description}
          </p>
        )}
        {seatDescription && <Benefits descriptions={seatDescription} />}
        <Amenities characteristics={characteristics} desktop={true} />
      </div>
    )}

    {useReservationFeeName && (
      <div
        className="gr-px-4 gr-text-gray-400"
        style={{ paddingTop: '8px', fontSize: '11px' }}
      >
        {reservationFee}
      </div>
    )}

    {/* Select button */}
    {!test_seatmap_one_click_desktop && SeatButton && (
      <div
        className="gr-px-4"
        style={{ paddingTop: '10px', paddingBottom: '14px' }}
      >
        {SeatButton}
      </div>
    )}
  </div>
);

// Legacy (pre-redesign) enhanced desktop seat details.
const DesktopEnhancedSeatDetailsComponentLegacy = ({
  desktopSeatDetailsClasses,
  imagesToDisplay,
  test_seatmap_one_click_desktop,
  hasSeatDescriptions,
  SeatButton,
  useReservationFeeName,
  reservationFee,
  description,
  seatDescription,
  characteristics,
  locale,
  rtlLangs,
  seat,
  display_name,
  test_seatmap_one_click,
  selected_seat,
  ariaPassenger,
  updateSelectedSeat,
  passenger_name,
  currency,
  displayPrice,
  priceFactor,
  decimal_places,
  usePriceAdjust,
  priceAdjustment,
}) => (
  <div id="desktop-seat-details" className={desktopSeatDetailsClasses}>
    {imagesToDisplay?.length > 0 ? (
      <Carousel
        interval={2000}
        autoPlay={true}
        infiniteLoop={true}
        showThumbs={false}
        showArrows={!test_seatmap_one_click_desktop}
        showIndicators={false}
        showStatus={false}
        className={'gr-mb-2 gr-mx-0'}
        width={'280px'}
      >
        {imagesToDisplay.map((img_url, index) => (
          <div key={index}>
            <img alt={''} src={img_url} className="gr-w-full gr-h-auto" />
          </div>
        ))}
      </Carousel>
    ) : (
      <div className="gr-pb-2"> </div>
    )}
    <div className="gr-px-3 gr-pb-3">
      <EnhancedTitleSection
        {...{
          locale,
          rtlLangs,
          seat,
          display_name,
          test_seatmap_one_click,
          selected_seat,
          ariaPassenger,
          updateSelectedSeat,
          passenger_name,
          currency,
          displayPrice,
          priceFactor,
          decimal_places,
          usePriceAdjust,
          priceAdjustment,
        }}
      />
    </div>
    {hasSeatDescriptions ? <div className="gr-border-b-2"></div> : null}
    <div className="gr-px-3">
      {!test_seatmap_one_click_desktop ? SeatButton : null}
      {useReservationFeeName && (
        <div
          className="gr-flex gr-pt-1 gr-justify-center gr-text-gray-500"
          style={{ fontSize: '11.2px' }}
        >
          {reservationFee}
        </div>
      )}
      <div>
        <div className="gr-mt-2 gr-text-gray-500 gr-hidden">{description}</div>
      </div>
      {seatDescription ? <Benefits descriptions={seatDescription} /> : null}
      <Amenities characteristics={characteristics} desktop={true} />
    </div>
  </div>
);

// #region DenseMobile

const DenseMobileSeatDetailsComponent = ({
  currency,
  displayPrice,
  priceFactor,
  decimal_places,
  usePriceAdjust,
  priceAdjustment,
  passenger,
  passengerInitials,
  travelerIndex,
  seat,
  test_seatmap_one_click_mobile,
  display_name,
  categoryFill,
  hasSeatBenefits,
  benefits,
  description,
  SeatButton,
  useReservationFeeName,
  reservationFee,
  selected_seat,
  ariaPassenger,
  updateSelectedSeat,
  characteristics,
}) => (
  <div id="dense-mobile-seat-details">
    {/* Handle bar */}
    <div className="gr-flex gr-justify-center gr-pt-3 gr-pb-1">
      <div
        className="gr-rounded-full"
        style={{ width: 40, height: 4, backgroundColor: '#D1D5DB' }}
      />
    </div>

    <div className="gr-px-4 gr-pb-5">
      {/* Top: seat name + price */}
      <div className="gr-flex gr-items-start gr-justify-between gr-py-3 gr-border-b gr-border-gray-100">
        <div>
          <h3 className="gr-text-xl gr-font-bold gr-text-gray-900">
            Seat {seat}
          </h3>
          {display_name && (
            <span className="gr-flex gr-items-center gr-gap-1 gr-mt-1 gr-text-sm gr-text-gray-500">
              {categoryFill && (
                <span
                  className="gr-rounded-full gr-flex-shrink-0"
                  style={{
                    width: '10px',
                    height: '10px',
                    backgroundColor: categoryFill,
                  }}
                />
              )}
              {display_name}
            </span>
          )}
          {passenger.infant_on_lap && (
            <span
              className="gr-inline-block gr-mt-1 gr-text-xs gr-text-white gr-rounded-full gr-px-2"
              style={{
                paddingTop: '2px',
                paddingBottom: '2px',
                backgroundColor: '#374151',
              }}
            >
              + infant
            </span>
          )}
        </div>
        <div className="gr-text-right gr-ml-4">
          <div className="gr-text-2xl gr-font-bold gr-text-gray-900">
            <Price
              currency={currency}
              amount={displayPrice * priceFactor}
              decimalPlaces={decimal_places}
            />
          </div>
          <div className="gr-text-xs gr-text-gray-400">per person</div>
          {usePriceAdjust && (
            <div className="gr-text-xs gr-text-gray-500 gr-mt-1">
              inc.{' '}
              <Price
                currency={currency}
                amount={displayPrice * (priceFactor - 1.0)}
                decimalPlaces={decimal_places}
              />{' '}
              {priceAdjustment.name}
            </div>
          )}
        </div>
      </div>

      {/* Passenger row */}
      <div
        className="gr-flex gr-items-center gr-py-3 gr-border-b gr-border-gray-100"
        style={{ gap: '12px' }}
      >
        <div
          className="gr-rounded-full gr-flex gr-items-center gr-justify-center gr-text-white gr-text-sm gr-font-bold gr-flex-shrink-0"
          style={{
            width: '36px',
            height: '36px',
            backgroundColor: 'var(--gordian-accent-color, #111827)',
          }}
        >
          {passengerInitials}
        </div>
        <div>
          <p className="gr-text-sm gr-font-medium gr-text-gray-900">
            {passenger.first_names} {passenger.surname}
          </p>
          {travelerIndex !== undefined && (
            <p className="gr-text-xs gr-text-gray-500">
              Traveler {travelerIndex + 1}
            </p>
          )}
        </div>
      </div>

      {/* Category description + Amenities */}
      <div className="gr-py-2">
        {description && (
          <p
            className="gr-text-xs gr-text-gray-500"
            style={{
              marginBottom: characteristics?.length > 0 ? '6px' : 0,
              lineHeight: '1.5',
            }}
          >
            {description}
          </p>
        )}
        <Amenities characteristics={characteristics} />
      </div>

      {useReservationFeeName && (
        <div className="gr-text-gray-400 gr-pb-2" style={{ fontSize: '11px' }}>
          {reservationFee}
        </div>
      )}

      {/* Buttons */}
      {!test_seatmap_one_click_mobile && SeatButton && (
        <div className="gr-mt-2">{SeatButton}</div>
      )}

      {/* Close for one-click */}
      {test_seatmap_one_click_mobile && (
        <button
          id="close-seat-details"
          aria-label={`Close details for seat ${selected_seat} for ${ariaPassenger}`}
          className="gr-absolute gr-top-4 gr-right-4 gr-p-1 gr-rounded-full gr-text-gray-400 hover:gr-text-gray-600"
          onClick={(e) => {
            e.preventDefault();
            updateSelectedSeat(null);
          }}
        >
          <Close alt="close" style={{ width: 16, height: 16 }} />
        </button>
      )}
    </div>
  </div>
);

// Legacy (pre-redesign) dense mobile seat details.
const DenseMobileSeatDetailsComponentLegacy = ({
  mobileSeatDetailsClass,
  currency,
  displayPrice,
  priceFactor,
  decimal_places,
  usePriceAdjust,
  priceAdjustment,
  passenger,
  lastInitial,
  seat,
  test_seatmap_one_click_mobile,
  display_name,
  hasSeatBenefits,
  benefits,
  description,
  SeatButton,
  useReservationFeeName,
  reservationFee,
  selected_seat,
  ariaPassenger,
  updateSelectedSeat,
  characteristics,
}) => (
  <div id="dense-mobile-seat-details" className={mobileSeatDetailsClass}>
    <div className="seat-detail gr-flex gr-items-center gr-justify-between gr-py-2">
      <div className="gr-flex gr-flex-col gr-justify-start gr-w-1/3">
        <span className="gr-flex gr-w-full gr-font-semibold gr-text-title">
          <Price
            currency={currency}
            amount={displayPrice * priceFactor}
            decimalPlaces={decimal_places}
          />
        </span>
        {usePriceAdjust && (
          <div className="gr-text-xs">
            (inc.{' '}
            <Price
              currency={currency}
              amount={displayPrice * (priceFactor - 1.0)}
              decimalPlaces={decimal_places}
            />{' '}
            {`${priceAdjustment.name}`})
          </div>
        )}
        <div className="gr-flex gr-pr-2">
          <span className="gr-text-secondary-text gr-truncate">
            {passenger.first_names}
          </span>
          <span className="gr-text-secondary-text gr-flex-shrink-0 gr-mx-1">
            {lastInitial}
          </span>
        </div>
        <span className="gr-flex gr-text-secondary-text">{seat} </span>
        {passenger.infant_on_lap && (
          <div className="gr-flex gr-text-xs gr-bg-gray-500 gr-text-white gr-px-1 gr-mr-1 gr-my-1 gr-rounded-sm">{`+ infant`}</div>
        )}
      </div>
      <div
        className={`gr-flex gr-flex-col gr-border-solid gr-border-l-2 gr-border-grey gr-w-full`}
      >
        <div className="gr-flex gr-flex-row gr-items-center">
          <div
            className={`gr-flex gr-flex-col ${
              test_seatmap_one_click_mobile ? '' : 'gr-w-2/3'
            } gr-pl-4 gr-pr-2 gr-justify-start`}
          >
            <span className="gr-flex gr-w-full gr-text-title">
              {display_name}
            </span>
            <span className="gr-flex gr-w-full gr-text-secondary-text">
              {hasSeatBenefits ? benefitsList(benefits) : description}
            </span>
          </div>
          {!test_seatmap_one_click_mobile && (
            <div className="gr-flex gr-flex-auto">{SeatButton}</div>
          )}
        </div>

        {useReservationFeeName && (
          <div
            className="gr-flex gr-justify-start gr-pt-1 gr-pl-4 gr-text-gray-500"
            style={{ fontSize: '10.4px' }}
          >
            {reservationFee}
          </div>
        )}
      </div>

      {test_seatmap_one_click_mobile && (
        <div
          className="gr-ml-2"
          style={{ position: 'absolute', top: '15%', right: '3.5%' }}
        >
          <button
            id="close-seat-details"
            aria-label={`Close details for seat ${selected_seat} for ${ariaPassenger}`}
            onClick={(e) => {
              e.preventDefault();
              updateSelectedSeat(null);
            }}
          >
            <Close
              alt="close"
              className="gr-cursor-pointer"
              style={{ width: 14, height: 14 }}
            />
          </button>
        </div>
      )}
    </div>
    <Amenities characteristics={characteristics} />
  </div>
);

// #region SeatDetails

export const SeatDetails = (props) => {
  const {
    basket,
    selectedSeat,
    selectedProduct,
    passengers = {},
    categories,
    currentSeatmap,
    acceptSeat,
    removeSeatFromBasket,
    updateSelectedSeat,
    nextPassenger,
    setCurrentPassenger,
    mobile = false,
    priceAdjustment = {},
    booleanizedVariants = {},
    variants,
    confirmBasket,
    locale,
    descriptions,
    useEnhancedDetails,
    exitModal,
    upsellModal,
    relativeWidthRatio,
    position,
  } = props;

  const detailsContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (evt) => {
      const id = evt.target.id;
      // Using `className` from SVG can be tricky, so check for `className.baseVal`
      const className =
        typeof evt.target.className === 'string'
          ? evt.target.className
          : evt.target.className?.baseVal || '';

      if (
        id === 'select-seat' ||
        className.includes('gr-exit-regulations') ||
        id === 'accept_exit_regulations' ||
        className.includes('gr-upsell_disclaimer') ||
        id === 'gr_upsell_disclaimer_accept_button'
      ) {
        return;
      }

      if (
        detailsContainerRef.current &&
        !detailsContainerRef.current.contains(evt.target)
      ) {
        updateSelectedSeat(null);
      }
    };

    if (selectedSeat) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedSeat, updateSelectedSeat]);

  const seatDescription =
    selectedSeat && descriptions
      ? descriptions.find(
          (d) => d.description_id === selectedSeat.description_id
        )
      : null;

  const {
    test_seatmap_one_click_desktop = true,
    test_seatmap_one_click_mobile = false,
    test_confirm_on_click = false,
  } = variants;

  if (!selectedSeat) {
    return null;
  }

  const {
    image_url = '',
    image_urls = [],
    display_name,
    description,
    benefits = [],
  } = categories[currentSeatmap]?.[selectedSeat.seat_category_id] ?? {};

  const imagesToDisplay = (
    image_urls?.length ? image_urls : [image_url]
  ).filter((img) => img);

  const { currentPassenger, listOfPassenger } = passengers;

  let passenger = currentPassenger;

  const [selected = null] = basket.seats.filter(
    (seat) =>
      seat.segment_id === currentSeatmap && seat.seat === selectedSeat.seat
  );

  if (selected) {
    [passenger] = listOfPassenger.filter(
      (passenger) => passenger.passenger_id === selected.passenger_id
    );
  }

  if (
    !selectedProduct.price_and_availability[passenger.passenger_id].available
  ) {
    return null;
  }

  let passenger_name = `${passenger.first_names} ${passenger.surname}`;

  const lastInitial =
    passenger?.surname?.length > 0
      ? `${passenger.surname.substring(0, 1)}.`
      : '';

  const { price_and_availability } = selectedProduct;
  const { seat, characteristics = [] } = selectedSeat;

  const { price } = price_and_availability[passenger.passenger_id];
  const { base, total, markups = [], taxes = [], fees = [] } = price;
  const { currency, decimal_places } = total;

  const priceFactor = priceAdjustment.factor ?? 1.0;
  const usePriceAdjust = priceFactor !== 1.0;
  const { test_reservation_fee_name = 'Convenience Fee' } = booleanizedVariants;
  const markupAmount = markups.reduce(
    (accumulator, curr) => accumulator + curr.amount,
    0
  );
  let convenienceFeeDisplayVariant =
    variants.convenienceFeeConfiguration?.seats?.seatDetailText;
  const hasMarkup = !!markupAmount;
  const useReservationFeeName =
    !!test_reservation_fee_name &&
    hasMarkup &&
    convenienceFeeDisplayVariant !== 'default';

  let reservationFeeTextPrefix = 'Includes ';
  let reservationFeeTextSuffix = '';
  if (convenienceFeeDisplayVariant === 'variant') {
    reservationFeeTextPrefix = '';
    reservationFeeTextSuffix = ' added at checkout';
  } else if (convenienceFeeDisplayVariant === 'full') {
    reservationFeeTextPrefix = '';
    reservationFeeTextSuffix = '';
  }
  // we only use this for Priceline, so it's ok that it only works for USD
  const markupIsMoreThanOneDollar =
    currency !== 'USD' || markupAmount / Math.pow(10, decimal_places) >= 1;

  const isSimple =
    convenienceFeeDisplayVariant !== 'default' && markupIsMoreThanOneDollar;
  let reservationFee = (
    <div className="gr-flex">
      <span>
        {reservationFeeTextPrefix}
        <Price
          currency={currency}
          amount={markupAmount}
          decimalPlaces={decimal_places}
          tabIndex={0}
          abbreviatePrice={convenienceFeeDisplayVariant !== 'default'}
          simple={isSimple}
        />{' '}
        {decodeURI(test_reservation_fee_name)}
        {reservationFeeTextSuffix}
      </span>
    </div>
  );
  if (
    convenienceFeeDisplayVariant &&
    !['variant', 'full', 'default'].includes(convenienceFeeDisplayVariant)
  ) {
    const seatDetailTextParts = convenienceFeeDisplayVariant.split('%{markup}');
    reservationFee = (
      <div className="gr-flex">
        <span>
          {seatDetailTextParts.map((part, index) => (
            <React.Fragment key={index}>
              {part}
              {index < seatDetailTextParts.length - 1 && (
                <Price
                  currency={currency}
                  amount={markupAmount}
                  decimalPlaces={decimal_places}
                  tabIndex={0}
                  abbreviatePrice={convenienceFeeDisplayVariant !== 'default'}
                  simple={isSimple}
                />
              )}
            </React.Fragment>
          ))}
        </span>
      </div>
    );
  }

  const isEnhancedSeatDetail = useEnhancedDetails;
  const current_passenger = props.passengers.currentPassenger;
  const selected_seat = props.selectedSeat.seat;
  const ariaPassenger = `${current_passenger.first_names} ${current_passenger.surname}`;

  const SeatButton = (
    <SeatSelectButton
      id="select-seat"
      handleClick={() => {
        if (selected) {
          removeSeatFromBasket({
            selectedSeat,
            currentSeatmap,
          });
          test_confirm_on_click && confirmBasket();
          setCurrentPassenger(passenger);
        } else {
          acceptSeat({
            currentSeatmap,
            selectedSeat,
            currentPassenger: passenger,
            selectedProduct,
          });
          nextPassenger(passenger);
        }
        updateSelectedSeat(null);
      }}
      selected={selected}
      aria_passenger={ariaPassenger}
      aria_seat={selected_seat}
    >
      {selected ? (
        <FormattedMessage id="remove_seat" defaultMessage="Remove seat" />
      ) : (
        <FormattedMessage id="select_seat" defaultMessage="Select seat" />
      )}
    </SeatSelectButton>
  );
  const seatDetailsPadding = isEnhancedSeatDetail
    ? 'gr-pb-4'
    : 'gr-p-3 gr-py-4';
  const desktopSeatDetailsClasses = useReservationFeeName
    ? 'seat-detail gr-px-3 gr-pt-4 gr-pb-2'
    : `seat-detail ${seatDetailsPadding}`;

  const airlineBasePrice =
    base.amount +
    taxes.reduce((accumulator, curr) => accumulator.amount + curr, 0) +
    fees.reduce((accumulator, curr) => accumulator.amount + curr, 0);
  const displayPrice = getDisplayPrice(
    convenienceFeeDisplayVariant,
    airlineBasePrice,
    total.amount
  );

  const test_seatmap_one_click = isMobile
    ? test_seatmap_one_click_mobile
    : test_seatmap_one_click_desktop;

  const hasSeatDescriptions = !!seatDescription?.texts?.some((texts) => {
    return texts.text && texts.text !== '';
  });

  const hasSeatBenefits = benefits?.length > 0;

  const passengerInitials =
    `${passenger?.first_names?.[0] ?? ''}${passenger?.surname?.[0] ?? ''}`.toUpperCase();

  const travelerIndex = listOfPassenger.findIndex(
    (p) => p.passenger_id === passenger.passenger_id
  );

  const categoryFill =
    categories[currentSeatmap]?.[selectedSeat.seat_category_id]?.fill;

  const useNewDesign = isNewSeatmapDesign(variants);
  const mobileSeatDetailsClass = 'gr-flex gr-flex-col gr-p-3 gr-py-2';

  const isMobileView = mobile || isMobile;
  // Use the modern bottom sheet for the new design on default-brand mobile.
  const useBottomSheet = useNewDesign && isMobileView;

  const renderSeatDetails = () => {
    if (isMobileView) {
      const denseMobileProps = {
        mobileSeatDetailsClass,
        lastInitial,
        currency,
        displayPrice,
        priceFactor,
        decimal_places,
        usePriceAdjust,
        priceAdjustment,
        passenger,
        passengerInitials,
        travelerIndex,
        seat,
        test_seatmap_one_click_mobile,
        display_name,
        categoryFill,
        hasSeatBenefits,
        benefits,
        description,
        SeatButton,
        useReservationFeeName,
        reservationFee,
        selected_seat,
        ariaPassenger,
        updateSelectedSeat,
        characteristics,
      };
      return useNewDesign ? (
        <DenseMobileSeatDetailsComponent {...denseMobileProps} />
      ) : (
        <DenseMobileSeatDetailsComponentLegacy {...denseMobileProps} />
      );
    }

    // Desktop views
    switch (true) {
      case isEnhancedSeatDetail: {
        const enhancedProps = {
          desktopSeatDetailsClasses,
          imagesToDisplay,
          test_seatmap_one_click_desktop,
          hasSeatDescriptions,
          SeatButton,
          useReservationFeeName,
          reservationFee,
          description,
          seatDescription,
          characteristics,
          locale,
          rtlLangs,
          seat,
          display_name,
          categoryFill,
          test_seatmap_one_click,
          selected_seat,
          ariaPassenger,
          updateSelectedSeat,
          passenger_name,
          passengerInitials,
          currency,
          displayPrice,
          priceFactor,
          decimal_places,
          usePriceAdjust,
          priceAdjustment,
        };
        return useNewDesign ? (
          <DesktopEnhancedSeatDetailsComponent {...enhancedProps} />
        ) : (
          <DesktopEnhancedSeatDetailsComponentLegacy {...enhancedProps} />
        );
      }
      default: {
        const desktopProps = {
          desktopSeatDetailsClasses,
          locale,
          rtlLangs,
          seat,
          test_seatmap_one_click,
          selected_seat,
          ariaPassenger,
          updateSelectedSeat,
          passenger_name,
          passengerInitials,
          display_name,
          categoryFill,
          imagesToDisplay,
          currency,
          displayPrice,
          priceFactor,
          decimal_places,
          usePriceAdjust,
          priceAdjustment,
          test_seatmap_one_click_desktop,
          SeatButton,
          useReservationFeeName,
          reservationFee,
          characteristics,
          description,
        };
        return useNewDesign ? (
          <DesktopSeatDetailsComponent {...desktopProps} />
        ) : (
          <DesktopSeatDetailsComponentLegacy {...desktopProps} />
        );
      }
    }
  };

  return (
    <MediaQuery minWidth={620 / relativeWidthRatio}>
      {(isLargeScreen) => (
        <FocusTrap
          focusTrapOptions={{ initialFocus: '#seat-details-container' }}
          // Add here when adding additional modals, else, the outside click handler on this component will fire.
          active={
            !mobile &&
            !isMobile &&
            isLargeScreen &&
            !exitModal &&
            !upsellModal &&
            !test_seatmap_one_click
          }
        >
          <div ref={detailsContainerRef}>
            <DetailsContainer
              current_passenger={current_passenger}
              selected_seat={selected_seat}
              mobile={mobile || isMobile}
              isEnhancedSeatDetail={isEnhancedSeatDetail}
              useBottomSheet={useBottomSheet}
              {...position}
            >
              {!useNewDesign && image_url && !isEnhancedSeatDetail ? (
                <img
                  className="gr-block gr-w-full gr-h-32 gr-object-cover"
                  alt={''}
                  src={image_url}
                ></img>
              ) : null}
              {renderSeatDetails()}
            </DetailsContainer>
          </div>
        </FocusTrap>
      )}
    </MediaQuery>
  );
};

const mapStateToProps = (state) => ({
  basket: state.basket,
  categories: state.categories,
  parentHeight: state.session.parentHeight,
  currentSeatmap: state.itinerary.currentSeatmap,
  useEnhancedDetails:
    state.itinerary.seatmapsBySegment[state.itinerary.currentSeatmap]
      ?.useEnhancedDetails,
  passengers: state.passengers,
  position: state.seatDetails.seat_details_position,
  selectedSeat: state.selectedSeat,
  selectedProduct:
    state.products.products?.seat?.[state.selectedSeat?.product_id] || null,
  exitModal: state.session.exitModal,
  booleanizedVariants: state.session.variants,
  priceAdjustment: state.session.priceAdjustment,
  reservationFeeName: state.session.reservationFeeName,
  upsellModal: state.session.upsellModal,
  variants: state.session.variants,
  relativeWidthRatio: state.session.relativeWidthRatio,
  locale: state.session.locale,
  descriptions:
    state.itinerary.seatmapsBySegment?.[state.itinerary.currentSeatmap]
      ?.descriptions,
});

const mapDispatchToProps = {
  acceptSeat,
  updateSelectedSeat,
  removeSeatFromBasket,
  nextPassenger,
  setCurrentPassenger,
  confirmBasket,
};

export default connect(mapStateToProps, mapDispatchToProps)(SeatDetails);
